import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import {
  Search, X, ChevronDown, ChevronUp, Users, Share2,
  Eye, GitBranch, Store, CheckCircle, AlertCircle,
  Trash2, Edit, Plus, CheckSquare, Square
} from 'lucide-react';

interface ReferralProfile {
  id: number;
  phone: string;
  name?: string | null;
  referralCode: string;
  totalDirectRefs: number;
  totalIndirectRefs: number;
  totalNetworkSize: number;
  totalOrderValue: number;
  totalRewardEarned?: number;
  tier: string;
  maxDepth?: number;
  createdAt?: number;
  isMerchant?: boolean;
  isBlocked?: boolean;
}

const TIER_STYLES: Record<string, string> = {
  bronze: 'bg-amber-100 text-amber-700',
  silver: 'bg-gray-100 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-purple-100 text-purple-700',
};

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

const SORT_OPTIONS = [
  { key: 'totalOrderValue', label: '帶來訂單金額' },
  { key: 'totalDirectRefs', label: '直接推薦數' },
  { key: 'totalIndirectRefs', label: '間接推薦數' },
  { key: 'totalNetworkSize', label: '網絡總人數' },
];

function formatDollars(dollars = 0): string {
  return `HK$${Math.round(dollars).toLocaleString('zh-HK')}`;
}

function normalizePhone(raw: string): string {
  let digits = raw.replace(/[^0-9]/g, '');
  if (digits.length === 8) digits = '852' + digits;
  if (digits.startsWith('852852')) digits = digits.substring(3);
  return digits;
}

const AdminReferrals: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can(PERMISSIONS.REFERRALS_WRITE);
  const canManageMerchants = can(PERMISSIONS.MERCHANTS_MANAGE);

  const [profiles, setProfiles] = useState<ReferralProfile[]>([]);
  const [allProfiles, setAllProfiles] = useState<ReferralProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [sortBy, setSortBy] = useState('totalOrderValue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkTier, setBulkTier] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ReferralProfile | null>(null);
  const [createForm, setCreateForm] = useState({ phone: '', name: '', referralCode: '' });
  const [editForm, setEditForm] = useState({ name: '', tier: 'bronze', isBlocked: false });
  const [submitting, setSubmitting] = useState(false);

  const [convertingPhone, setConvertingPhone] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    setPage(1);
  }, [search, tier, sortBy, sortOrder]);

  // Clear selection when page/filter changes to avoid stale selections
  useEffect(() => {
    setSelectedPhones(new Set());
  }, [page, search, tier, sortBy, sortOrder]);

  const loadProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const listParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search,
        tier,
        sortBy,
        sortOrder,
      });
      const listRes = await apiFetch('/api/public/admin/referrals?' + listParams.toString());
      if (!listRes.ok) throw new Error('獲取推薦列表失敗');
      const listJson = await listRes.json();
      setProfiles(Array.isArray(listJson.data) ? listJson.data : []);
      setTotal(listJson.total || 0);

      const statsParams = new URLSearchParams({
        page: '1',
        limit: '100',
        search,
        tier,
        sortBy: 'totalOrderValue',
        sortOrder: 'desc',
      });
      const statsRes = await apiFetch('/api/public/admin/referrals?' + statsParams.toString());
      if (statsRes.ok) {
        const statsJson = await statsRes.json();
        setAllProfiles(Array.isArray(statsJson.data) ? statsJson.data : []);
      }
    } catch (e: any) {
      setError(e.message || '加載失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const listParams = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          search,
          tier,
          sortBy,
          sortOrder,
        });
        const listRes = await apiFetch('/api/public/admin/referrals?' + listParams.toString());
        if (!listRes.ok) throw new Error('獲取推薦列表失敗');
        const listJson = await listRes.json();
        if (!cancelled) {
          setProfiles(Array.isArray(listJson.data) ? listJson.data : []);
          setTotal(listJson.total || 0);
        }

        const statsParams = new URLSearchParams({
          page: '1',
          limit: '100',
          search,
          tier,
          sortBy: 'totalOrderValue',
          sortOrder: 'desc',
        });
        const statsRes = await apiFetch('/api/public/admin/referrals?' + statsParams.toString());
        if (statsRes.ok) {
          const statsJson = await statsRes.json();
          if (!cancelled) {
            setAllProfiles(Array.isArray(statsJson.data) ? statsJson.data : []);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [page, limit, search, tier, sortBy, sortOrder]);

  const showToast = (success?: string, err?: string) => {
    if (success) setActionSuccess(success);
    if (err) setActionError(err);
    setTimeout(() => { setActionSuccess(null); setActionError(null); }, 4000);
  };

  const toggleSelect = (phone: string) => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visiblePhones = profiles.map(p => p.phone);
    const allSelected = visiblePhones.length > 0 && visiblePhones.every(p => selectedPhones.has(p));
    if (allSelected) {
      setSelectedPhones(prev => {
        const next = new Set(prev);
        visiblePhones.forEach(p => next.delete(p));
        return next;
      });
    } else {
      setSelectedPhones(prev => {
        const next = new Set(prev);
        visiblePhones.forEach(p => next.add(p));
        return next;
      });
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedPhones.size === 0) return;
    if (bulkAction === 'change-tier' && !bulkTier) return;

    const actionLabel: Record<string, string> = {
      block: '封鎖',
      unblock: '解封',
      delete: '刪除',
      'change-tier': '變更等級',
      'convert-to-merchant': '升級商家',
    };

    if (!window.confirm(`確定對已選的 ${selectedPhones.size} 位推薦人執行「${actionLabel[bulkAction]}」？`)) return;

    setBulkLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/referrals/bulk', {
        method: 'POST',
        body: JSON.stringify({
          phones: Array.from(selectedPhones),
          action: bulkAction,
          tier: bulkTier,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        const failed = (json.results || []).filter((r: any) => !r.success);
        if (failed.length === 0) {
          showToast(`已成功對 ${json.successCount} 位推薦人執行 ${actionLabel[bulkAction]}`);
        } else {
          showToast(
            `完成：${json.successCount} 成功，${failed.length} 失敗`,
            failed.map((f: any) => `${f.phone}: ${f.error}`).join('; ')
          );
        }
        setSelectedPhones(new Set());
        setBulkAction('');
        setBulkTier('');
        await loadProfiles();
      } else {
        showToast(undefined, json.error || '批量操作失敗');
      }
    } catch (e: any) {
      showToast(undefined, e.message || '網絡錯誤');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = normalizePhone(createForm.phone);
    if (!/^852\d{8}$/.test(phone)) {
      showToast(undefined, '請輸入有效的香港手提電話號碼');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/public/admin/referrals', {
        method: 'POST',
        body: JSON.stringify({
          phone,
          name: createForm.name.trim() || null,
          referralCode: createForm.referralCode.trim().toUpperCase() || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast('推薦檔案已建立');
        setShowCreate(false);
        setCreateForm({ phone: '', name: '', referralCode: '' });
        await loadProfiles();
      } else {
        showToast(undefined, json.error || '建立失敗');
      }
    } catch (e: any) {
      showToast(undefined, e.message || '網絡錯誤');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (profile: ReferralProfile) => {
    setEditingProfile(profile);
    setEditForm({
      name: profile.name || '',
      tier: profile.tier || 'bronze',
      isBlocked: !!profile.isBlocked,
    });
    setShowEdit(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProfile) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(editingProfile.phone)}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name.trim() || null,
          tier: editForm.tier,
          isBlocked: editForm.isBlocked,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast('推薦檔案已更新');
        setShowEdit(false);
        setEditingProfile(null);
        await loadProfiles();
      } else {
        showToast(undefined, json.error || '更新失敗');
      }
    } catch (e: any) {
      showToast(undefined, e.message || '網絡錯誤');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (phone: string) => {
    if (!window.confirm(`確定刪除推薦人 ${phone}？此操作不可復原。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}`, {
        method: 'DELETE',
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast('已刪除');
        await loadProfiles();
      } else {
        showToast(undefined, json.error || '刪除失敗');
      }
    } catch (e: any) {
      showToast(undefined, e.message || '網絡錯誤');
    }
  };

  const handleConvertToMerchant = async (phone: string) => {
    if (!window.confirm('確定將此推薦人轉為「鄰里商家」？轉換後將取消未發放的推薦獎賞，並改以 15% 商家佣金模式運作。')) return;
    setConvertingPhone(phone);
    try {
      const res = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}/convert-to-merchant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        showToast(`已成功轉換：${phone}`);
        await loadProfiles();
      } else {
        showToast(undefined, json.error || '轉換失敗');
      }
    } catch (e: any) {
      showToast(undefined, e.message || '網絡錯誤');
    } finally {
      setConvertingPhone(null);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const totalReferrers = total;
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const thisMonthCount = allProfiles.filter(
    (p) => (p.createdAt || 0) * 1000 >= startOfMonth.getTime()
  ).length;
  const networkOrderValue = allProfiles.reduce(
    (sum, p) => sum + (p.totalOrderValue || 0),
    0
  );
  const avgDepth = allProfiles.length
    ? allProfiles.reduce((sum, p) => sum + (p.maxDepth || 0), 0) / allProfiles.length
    : 0;

  const visibleAllSelected = profiles.length > 0 && profiles.every(p => selectedPhones.has(p.phone));

  return (
    <AdminLayout currentPage="referrals" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Share2 className="w-6 h-6 text-orange-600" />
          推薦網絡
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          管理客戶推薦關係與網絡表現
        </p>
      </div>

      {actionSuccess && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm flex items-start gap-2">
          <CheckCircle size={18} className="shrink-0 mt-0.5" />
          {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          {actionError}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">總推薦人數</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            {totalReferrers.toLocaleString('zh-HK')}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">本月新增推薦關係</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            {thisMonthCount.toLocaleString('zh-HK')}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">網絡總訂單金額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatDollars(networkOrderValue)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">平均網絡深度</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{avgDepth.toFixed(1)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="姓名、電話、推薦碼..."
                className="w-full pl-9 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">等級</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              <option value="">全部等級</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">排序</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-3">
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {sortOrder === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              {sortOrder === 'desc' ? '降序' : '升序'}
            </button>
            {canWrite && (
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-1 bg-orange-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
              >
                <Plus size={16} /> 新增推薦人
              </button>
            )}
            <div className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-bold text-gray-800 dark:text-white">{total}</span> 位
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {canWrite && selectedPhones.size > 0 && (
        <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
            已選 {selectedPhones.size} 位
          </span>
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="">選擇批量動作...</option>
            <option value="block">封鎖</option>
            <option value="unblock">解封</option>
            <option value="change-tier">變更等級</option>
            {canManageMerchants && <option value="convert-to-merchant">升級商家</option>}
            <option value="delete">刪除</option>
          </select>
          {bulkAction === 'change-tier' && (
            <select
              value={bulkTier}
              onChange={(e) => setBulkTier(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="">選擇等級</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          )}
          <button
            onClick={handleBulkAction}
            disabled={bulkLoading || !bulkAction || (bulkAction === 'change-tier' && !bulkTier)}
            className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {bulkLoading ? '執行中...' : '執行'}
          </button>
          <button
            onClick={() => setSelectedPhones(new Set())}
            className="text-sm text-blue-700 dark:text-blue-300 hover:underline"
          >
            清除選擇
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                {canWrite && (
                  <th className="p-4 w-10">
                    <button onClick={toggleSelectAll} className="focus:outline-none">
                      {visibleAllSelected ? (
                        <CheckSquare size={18} className="text-orange-600" />
                      ) : (
                        <Square size={18} className="text-gray-400" />
                      )}
                    </button>
                  </th>
                )}
                <th className="p-4">推薦人</th>
                <th className="p-4">推薦碼</th>
                <th className="p-4">等級</th>
                <th className="p-4 text-center">直接推薦</th>
                <th className="p-4 text-center">間接網絡</th>
                <th className="p-4 text-right">帶來訂單</th>
                <th className="p-4">狀態</th>
                <th className="p-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={canWrite ? 9 : 8} className="p-8 text-center text-gray-400">加載中...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={canWrite ? 9 : 8} className="p-8 text-center text-red-500">{error}</td>
                </tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 9 : 8} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無推薦人數據
                  </td>
                </tr>
              ) : (
                profiles.map((p) => (
                  <tr key={p.phone} className={`hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors ${p.isBlocked ? 'opacity-60' : ''}`}>
                    {canWrite && (
                      <td className="p-4">
                        <button onClick={() => toggleSelect(p.phone)} className="focus:outline-none">
                          {selectedPhones.has(p.phone) ? (
                            <CheckSquare size={18} className="text-orange-600" />
                          ) : (
                            <Square size={18} className="text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center text-orange-600 dark:text-orange-400 text-sm font-bold">
                          {p.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-300">{p.name || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{p.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm font-mono text-gray-700 dark:text-gray-300">{p.referralCode}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_STYLES[p.tier?.toLowerCase()] || TIER_STYLES.bronze}`}>
                        {TIER_LABELS[p.tier?.toLowerCase()] || p.tier}
                      </span>
                    </td>
                    <td className="p-4 text-center text-sm font-medium text-gray-800 dark:text-white">{p.totalDirectRefs}</td>
                    <td className="p-4 text-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-800 dark:text-white">{p.totalNetworkSize}</span>
                      <span className="text-xs ml-1">(直接 {p.totalDirectRefs} + 間接 {p.totalIndirectRefs})</span>
                    </td>
                    <td className="p-4 text-right text-sm font-bold text-gray-800 dark:text-white">{formatDollars(p.totalOrderValue)}</td>
                    <td className="p-4">
                      {p.isMerchant ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                          <Store size={12} /> 鄰里商家
                        </span>
                      ) : p.isBlocked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          已封鎖
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="p-4 space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/admin/referrals/${encodeURIComponent(p.phone)}`)}
                        className="inline-flex items-center gap-1 bg-orange-600 text-white px-3 py-1.5 rounded text-xs hover:bg-orange-700 transition-colors"
                      >
                        <Eye size={12} /> 詳情
                      </button>
                      <button
                        onClick={() => navigate(`/admin/referrals/${encodeURIComponent(p.phone)}/tree`)}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 transition-colors"
                      >
                        <GitBranch size={12} /> 鏈路圖
                      </button>
                      {canWrite && (
                        <>
                          <button
                            onClick={() => openEdit(p)}
                            className="inline-flex items-center gap-1 bg-gray-600 text-white px-3 py-1.5 rounded text-xs hover:bg-gray-700 transition-colors"
                          >
                            <Edit size={12} /> 編輯
                          </button>
                          <button
                            onClick={() => handleDelete(p.phone)}
                            className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 rounded text-xs hover:bg-red-700 transition-colors"
                          >
                            <Trash2 size={12} /> 刪除
                          </button>
                        </>
                      )}
                      {canManageMerchants && !p.isMerchant && (
                        <button
                          onClick={() => handleConvertToMerchant(p.phone)}
                          disabled={convertingPhone === p.phone}
                          className="inline-flex items-center gap-1 bg-stone-700 text-white px-3 py-1.5 rounded text-xs hover:bg-stone-800 transition-colors disabled:opacity-60"
                        >
                          {convertingPhone === p.phone ? '處理中...' : <><Store size={12} /> 升級商家</>}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              第 {page} / {totalPages} 頁
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">新增推薦人</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">電話 *</label>
                <input
                  type="tel"
                  required
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="例如 51234567"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">姓名</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">推薦碼（選填，系統可自動生成）</label>
                <input
                  type="text"
                  value={createForm.referralCode}
                  onChange={(e) => setCreateForm({ ...createForm, referralCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white uppercase"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-60"
                >
                  {submitting ? '儲存中...' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">編輯推薦人</h3>
              <button onClick={() => setShowEdit(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">姓名</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">等級</label>
                <select
                  value={editForm.tier}
                  onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white"
                >
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.isBlocked}
                  onChange={(e) => setEditForm({ ...editForm, isBlocked: e.target.checked })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">封鎖此推薦人</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEdit(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-60"
                >
                  {submitting ? '儲存中...' : '儲存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReferrals;
